/**
 * API keys — mint, inspect, and revoke keys for the public REST API
 * (billable 'api' feature; surface lives at /api/public/v1).
 *
 * Tier is derived from who you are + where you're standing, never from a
 * picker:
 *   - office_manager                → customer key for their own account.
 *   - reseller_owner (no context)   → reseller key (sees all their customers).
 *   - reseller_owner (in customer)  → customer key for that customer.
 *   - super_admin (in customer)     → customer key for that customer.
 *   - super_admin (no context)      → PLATFORM key (cross-tenant wildcard).
 *
 * The plaintext is shown exactly once, in the post-create reveal panel —
 * after the dialog closes only the ivk_XXXX prefix remains.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiKeys, API_SCOPE_CATALOG,
  type ApiKeyRow, type ApiKeyTier, type CreateApiKeyResponse,
} from '@/api/apiKeys';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  KeyRound, Plus, Copy, Check, AlertTriangle, BookOpen, Ban, Globe2, Building2, Briefcase,
} from 'lucide-react';

// ── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - Date.parse(iso);
  if (ms < 90_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 60) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const TIER_META: Record<ApiKeyTier, { label: string; icon: typeof Globe2; className: string }> = {
  platform: { label: 'Platform', icon: Globe2, className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-transparent' },
  reseller: { label: 'Reseller', icon: Building2, className: 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-transparent' },
  customer: { label: 'Customer', icon: Briefcase, className: '' },
};

function TierBadge({ tier }: { tier: ApiKeyTier }) {
  const meta = TIER_META[tier];
  const Icon = meta.icon;
  return (
    <Badge variant={tier === 'customer' ? 'default' : 'outline'} className={`text-[10px] gap-1 ${meta.className}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </Badge>
  );
}

function ScopeChips({ scopes }: { scopes: string[] }) {
  const shown = scopes.slice(0, 3);
  const extra = scopes.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((s) => (
        <code key={s} className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-secondary-foreground">
          {s}
        </code>
      ))}
      {extra > 0 && (
        <span className="text-[10px] text-muted-foreground" title={scopes.join(', ')}>+{extra} more</span>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function ApiKeysPage() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  // What tier will a freshly minted key have, given role + context?
  const newKeyTier: ApiKeyTier = effectiveCustomerId
    ? 'customer'
    : user.is_super_admin
      ? 'platform'
      : user.role === 'reseller_owner'
        ? 'reseller'
        : 'customer';

  const { data, isLoading } = useQuery({
    queryKey: ['api-keys', effectiveCustomerId, showRevoked],
    queryFn: () => apiKeys.list({
      customer_id: effectiveCustomerId ?? undefined,
      include_revoked: showRevoked,
    }),
  });
  const rows: ApiKeyRow[] = useMemo(() => data?.rows ?? [], [data]);

  const revoke = useMutation({
    mutationFn: (id: string) => apiKeys.revoke(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast({ title: 'Key revoked', description: 'Requests with this key now fail with 401.' });
      setRevokeTarget(null);
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not revoke key',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const columns = [
    {
      header: 'Key',
      cell: (r: ApiKeyRow) => (
        <div className="min-w-0">
          <div className={`font-medium text-sm ${r.revoked_at ? 'text-muted-foreground line-through' : ''}`}>
            {r.name}
          </div>
          <div className="font-mono text-[11px] text-muted-foreground">
            {r.key_prefix ?? 'ivk_????'}
            <span className="tracking-widest">····</span>
          </div>
        </div>
      ),
    },
    { header: 'Tier', cell: (r: ApiKeyRow) => <TierBadge tier={r.tier} /> },
    { header: 'Scopes', cell: (r: ApiKeyRow) => <ScopeChips scopes={r.scopes} /> },
    {
      header: 'Rate limit',
      cell: (r: ApiKeyRow) => (
        <span className="font-mono text-xs">{r.rate_limit_per_min}/min</span>
      ),
    },
    {
      header: 'Last used',
      cell: (r: ApiKeyRow) => (
        <span className="text-xs text-muted-foreground">{timeAgo(r.last_used_at)}</span>
      ),
    },
    {
      header: 'Status',
      cell: (r: ApiKeyRow) =>
        r.revoked_at
          ? <Badge variant="secondary" className="text-[10px]">revoked</Badge>
          : <Badge variant="default" className="text-[10px]">active</Badge>,
    },
    {
      header: '',
      cell: (r: ApiKeyRow) =>
        r.revoked_at ? null : (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-destructive hover:text-destructive"
            onClick={() => setRevokeTarget(r)}
          >
            <Ban className="h-3.5 w-3.5 mr-1.5" /> Revoke
          </Button>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Settings"
        title="API keys"
        description="Programmatic access to your account over the public REST API. Keys are shown once at creation — store them like passwords."
        actions={(
          <>
            <Button variant="outline" asChild>
              <a href="/api/public/v1/docs" target="_blank" rel="noreferrer">
                <BookOpen className="h-4 w-4 mr-1.5" /> API reference
              </a>
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> New key
            </Button>
          </>
        )}
      />

      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">
          Base URL <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[11px]">/api/public/v1</code>
          {' '}· authenticate with <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[11px]">Authorization: Bearer ivk_…</code>
        </p>
        <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={showRevoked} onCheckedChange={setShowRevoked} />
          Show revoked
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        empty={isLoading ? 'Loading…' : (
          <div className="space-y-2">
            <KeyRound className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
            <p>No API keys yet.</p>
            <p className="text-xs">
              Mint one to integrate CRMs, dashboards, or automations with your account.
            </p>
          </div>
        )}
      />

      {createOpen && (
        <CreateKeyDialog
          tier={newKeyTier}
          customerId={effectiveCustomerId}
          onClose={() => {
            qc.invalidateQueries({ queryKey: ['api-keys'] });
            setCreateOpen(false);
          }}
        />
      )}

      {revokeTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setRevokeTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                Revoke this key?
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm py-2">
              <span className="font-medium">{revokeTarget.name}</span>{' '}
              <span className="font-mono text-xs text-muted-foreground">
                ({revokeTarget.key_prefix}····)
              </span>{' '}
              stops working immediately. Anything still calling with it will
              receive <code className="font-mono text-xs">401</code>. This cannot be undone.
            </p>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={revoke.isPending}>Cancel</Button>
              </DialogClose>
              <Button
                variant="destructive"
                onClick={() => revoke.mutate(revokeTarget.id)}
                disabled={revoke.isPending}
              >
                {revoke.isPending ? 'Revoking…' : 'Revoke key'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Create dialog (form phase → one-time reveal phase) ─────────────────────

function CreateKeyDialog({ tier, customerId, onClose }: {
  tier: ApiKeyTier;
  customerId: string | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [rateLimit, setRateLimit] = useState('60');
  const [err, setErr] = useState<string | null>(null);
  const [minted, setMinted] = useState<CreateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const isPlatform = tier === 'platform';

  const create = useMutation({
    mutationFn: () => apiKeys.create({
      name: name.trim(),
      scopes: isPlatform ? ['platform'] : scopes,
      rate_limit_per_min: Math.min(Math.max(Number(rateLimit) || 60, 1), 10_000),
      // Forward the entered-customer id whenever we have one — never gate
      // on picker visibility (CLAUDE.md customer-context rule).
      ...(customerId ? { customer_id: customerId } : {}),
    }),
    onSuccess: (res) => setMinted(res),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Failed to create key'),
  });

  function toggleScope(scope: string) {
    setScopes((prev) => prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    setErr(null);
    if (!name.trim()) { setErr('Give the key a name (e.g. "CRM sync").'); return; }
    if (!isPlatform && scopes.length === 0) { setErr('Pick at least one scope.'); return; }
    create.mutate();
  }

  async function copyKey() {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(minted.plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ title: 'Copy failed', description: 'Select the key text and copy manually.', variant: 'destructive' });
    }
  }

  const tierNote: Record<ApiKeyTier, string> = {
    platform: 'Platform key — full cross-tenant access to every reseller and customer. Guard it accordingly.',
    reseller: 'Reseller key — sees all of your customers. Narrow requests with ?customer_id.',
    customer: 'Customer key — pinned to this customer account only.',
  };

  // ── Reveal phase — the only time the plaintext ever exists client-side ──
  if (minted) {
    return (
      <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> Key created
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div className="rounded-lg bg-slate-950 p-4 dark:border dark:border-slate-800">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  {minted.api_key.name}
                </span>
                <TierBadge tier={minted.api_key.tier} />
              </div>
              <code className="block break-all font-mono text-sm leading-relaxed text-emerald-400">
                {minted.plaintext}
              </code>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="mt-3 w-full"
                onClick={copyKey}
              >
                {copied
                  ? <><Check className="h-3.5 w-3.5 mr-1.5 text-emerald-600" /> Copied</>
                  : <><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy key</>}
              </Button>
            </div>

            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                This is the <strong>only time</strong> the full key is shown.
                Store it in a secrets manager — if it's lost, revoke it and
                mint a new one.
              </span>
            </div>

            <p className="text-xs text-muted-foreground">
              Use it as{' '}
              <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[11px]">
                Authorization: Bearer {minted.api_key.key_prefix}…
              </code>
            </p>
          </div>

          <DialogFooter>
            <Button onClick={onClose}>Done — I saved the key</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Form phase ──────────────────────────────────────────────────────────
  const readScopes = API_SCOPE_CATALOG.filter((s) => s.kind === 'read');
  const writeScopes = API_SCOPE_CATALOG.filter((s) => s.kind === 'write');

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New API key</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-md bg-secondary/50 p-3 text-xs text-muted-foreground">
            {tierNote[tier]}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="api-key-name">Name</Label>
            <Input
              id="api-key-name"
              placeholder='e.g. "CRM sync" or "Reporting dashboard"'
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoFocus
            />
          </div>

          {!isPlatform && (
            <div className="space-y-2">
              <Label>Scopes</Label>
              <div className="max-h-56 space-y-3 overflow-y-auto rounded-md border border-border p-3">
                <ScopeGroup title="Read" scopes={readScopes} selected={scopes} onToggle={toggleScope} />
                <ScopeGroup title="Write" scopes={writeScopes} selected={scopes} onToggle={toggleScope} />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="api-key-rate">Rate limit (requests per minute)</Label>
            <Input
              id="api-key-rate"
              type="number"
              min={1}
              max={10000}
              value={rateLimit}
              onChange={(e) => setRateLimit(e.target.value)}
              className="max-w-[10rem]"
            />
          </div>

          {err && (
            <p role="alert" className="text-sm text-destructive">{err}</p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ScopeGroup({ title, scopes, selected, onToggle }: {
  title: string;
  scopes: typeof API_SCOPE_CATALOG;
  selected: string[];
  onToggle: (scope: string) => void;
}) {
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {title}
      </div>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {scopes.map((s) => (
          <label
            key={s.scope}
            className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 text-xs transition-colors hover:bg-secondary/40 has-[[data-state=checked]]:border-primary/50 has-[[data-state=checked]]:bg-primary/5"
          >
            <Checkbox
              checked={selected.includes(s.scope)}
              onCheckedChange={() => onToggle(s.scope)}
              className="mt-0.5"
            />
            <span className="min-w-0">
              <span className="block font-medium">{s.label}</span>
              <code className="block truncate font-mono text-[10px] text-muted-foreground">{s.scope}</code>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
